import posthog from 'posthog-js';
import { getRuntimeConfig } from './runtimeConfig';
import {
  sanitizeCurrentUrl,
  sanitizeEvent,
  sanitizeReferrer,
} from './urlSanitizer';

let initialized = false;
let lastTrackedPageview;

export const initializePostHog = () => {
  const runtimeConfig = getRuntimeConfig();
  const token =
    typeof runtimeConfig.posthogProjectToken === 'string'
      ? runtimeConfig.posthogProjectToken.trim()
      : '';

  if (!token || initialized) {
    return token ? posthog : null;
  }

  try {
    posthog.init(token, {
      api_host: runtimeConfig.posthogHost,
      ui_host: runtimeConfig.posthogUiHost,
      defaults: '2026-05-30',
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      opt_out_capturing_persistence_type: 'localStorage',
      respect_dnt: true,
      person_profiles: 'identified_only',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      disable_surveys: true,
      disable_external_dependency_loading: true,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_exceptions: false,
      capture_performance: false,
      advanced_disable_flags: true,
      get_current_url: sanitizeCurrentUrl,
      before_send: sanitizeEvent,
      property_denylist: [
        'name',
        'email',
        'phone',
        'company',
        'from',
        'answers',
        'error',
        'errors',
        'message',
        'stack',
        'exception',
      ],
    });
    initialized = true;
    return posthog;
  } catch (error) {
    return null;
  }
};

export const getPostHogClient = () =>
  initialized ? posthog : initializePostHog();

const capturePageView = (client) => {
  try {
    if (client.is_capturing?.() === false) {
      return;
    }

    const currentUrl = sanitizeCurrentUrl();
    if (currentUrl === lastTrackedPageview) {
      return;
    }

    client.capture('$pageview', {
      $current_url: currentUrl,
      $referrer: sanitizeReferrer(),
    });
    lastTrackedPageview = currentUrl;
  } catch (error) {
    // Analytics is intentionally best effort.
  }
};

export const trackPostHogPageView = () => {
  const client = getPostHogClient();

  if (client) {
    capturePageView(client);
  }
};

export const applyPostHogConsent = (consent) => {
  const client = getPostHogClient();

  if (!client) {
    return;
  }

  try {
    if (consent === 'accepted') {
      client.opt_in_capturing({ captureEventName: false });
      capturePageView(client);
    } else {
      client.opt_out_capturing();
      lastTrackedPageview = undefined;
    }
  } catch (error) {
    // A blocked analytics endpoint must never affect the website.
  }
};

export const revokePostHogConsent = () => {
  const client = getPostHogClient();

  if (!client) {
    return;
  }

  try {
    client.reset(true);
    client.opt_out_capturing();
    lastTrackedPageview = undefined;
  } catch (error) {
    // A blocked analytics endpoint must never affect the website.
  }
};

export const getSanitizedReferrer = sanitizeReferrer;
